import { createFileRoute } from '@tanstack/react-router'
import { RefreshCw, Building2, Users, Briefcase, BarChart3, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { DepartmentTree, DepartmentDetail } from '@/components/organization/department-tree'
import { ExportButton } from '@/components/organization/export-button'
import { StatisticsCards } from '@/components/organization/statistics-cards'
import { StatisticsOverview } from '@/components/organization/statistics-overview'
import { TeamResourceList, TeamResourceDetail } from '@/components/organization/team-resource-view'
import {
  useOrgDepartmentTree,
  useOrganizationStatistics,
  useTeamResources,
  type OrgDepartmentTreeNode,
  type TeamResourceView,
} from '@/hooks/hr'

export const Route = createFileRoute('/_authenticated/organization/')({
  component: OrganizationPage,
})

function OrganizationPage() {
  const [activeTab, setActiveTab] = useState<'departments' | 'teams' | 'statistics'>('departments')
  const [selectedDepartment, setSelectedDepartment] = useState<OrgDepartmentTreeNode | null>(null)
  const [selectedTeam, setSelectedTeam] = useState<TeamResourceView | null>(null)

  const { data: statistics, isLoading: statsLoading, refetch: refetchStats } = useOrganizationStatistics()
  const { data: departmentTree, isLoading: treeLoading, refetch: refetchTree } = useOrgDepartmentTree()
  const { data: teamResources, isLoading: teamsLoading, refetch: refetchTeams } = useTeamResources()

  const handleRefresh = () => {
    void refetchStats()
    void refetchTree()
    void refetchTeams()
  }

  const handleDepartmentClick = (departmentId: string) => {
    // 切换到部门 Tab 并选中对应部门
    setActiveTab('departments')
    const findDepartment = (nodes: OrgDepartmentTreeNode[]): OrgDepartmentTreeNode | null => {
      for (const node of nodes) {
        if (node.id === departmentId) {
          return node
        }
        const found = findDepartment(node.children)
        if (found) {
          return found
        }
      }
      return null
    }
    if (departmentTree) {
      const dept = findDepartment(departmentTree)
      if (dept) {
        setSelectedDepartment(dept)
      }
    }
  }

  const handleTeamClick = (teamId: string) => {
    // 切换到团队 Tab 并选中对应团队
    setActiveTab('teams')
    if (teamResources) {
      const team = teamResources.find((t) => t.id === teamId)
      if (team) {
        setSelectedTeam(team)
      }
    }
  }

  const tabs = [
    { id: 'departments' as const, label: '部门架构', icon: Building2 },
    { id: 'teams' as const, label: '团队资源', icon: Briefcase },
    { id: 'statistics' as const, label: '统计概览', icon: BarChart3 },
  ]

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">组织架构</h1>
          <p className="text-muted-foreground">查看公司组织结构和人员分布</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton />
          <button
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm font-medium hover:bg-muted transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
        </div>
      </div>

      {/* 统计卡片 */}
      {statsLoading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-lg border bg-card p-4">
              <div className="h-16 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : statistics ? (
        <StatisticsCards statistics={statistics} />
      ) : null}

      {/* Tab 切换 */}
      <div>
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${activeTab === tab.id
                ? 'bg-background shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
                }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* 部门架构 Tab */}
        {activeTab === 'departments' && (
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <div className="rounded-lg border bg-card lg:col-span-2">
              <div className="border-b p-4">
                <h2 className="font-semibold">部门层级</h2>
              </div>
              <div className="p-4">
                {treeLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : departmentTree && departmentTree.length > 0 ? (
                  <DepartmentTree
                    data={departmentTree}
                    onSelect={setSelectedDepartment}
                    selectedId={selectedDepartment?.id}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Building2 className="mb-2 h-12 w-12" />
                    <p>暂无部门数据</p>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-lg border bg-card">
              <div className="border-b p-4">
                <h2 className="font-semibold">部门详情</h2>
              </div>
              <div className="p-4">
                {selectedDepartment ? (
                  <DepartmentDetail department={selectedDepartment} />
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Building2 className="mb-2 h-8 w-8" />
                    <p className="text-sm">点击左侧部门查看详情</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 团队资源 Tab */}
        {activeTab === 'teams' && (
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {teamsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : teamResources ? (
                <TeamResourceList
                  teams={teamResources}
                  onTeamClick={setSelectedTeam}
                />
              ) : null}
            </div>

            <div className="rounded-lg border bg-card">
              <div className="border-b p-4">
                <h2 className="font-semibold">团队详情</h2>
              </div>
              <div className="p-4">
                {selectedTeam ? (
                  <TeamResourceDetail team={selectedTeam} />
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Users className="mb-2 h-8 w-8" />
                    <p className="text-sm">点击左侧团队查看详情</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 统计概览 Tab */}
        {activeTab === 'statistics' && (
          <div className="mt-4">
            <StatisticsOverview
              onDepartmentClick={handleDepartmentClick}
              onTeamClick={handleTeamClick}
            />
          </div>
        )}
      </div>
    </div>
  )
}
